/**
 * Deep clone utility
 * 
 * Provides deep copying functionality for state snapshots and data structures.
 * Handles Proxy objects by serializing through JSON (which strips proxies).
 * Uses structuredClone when available, falls back to JSON serialization.
 */

/**
 * Deep clone a value, handling Proxy objects and cyclical references.
 * 
 * Note: Proxies are automatically unwrapped during JSON serialization,
 * which makes this safe for cloning reactive state objects.
 */
export function deepClone<T>(x: T): T {
  // For objects (including Proxies), use JSON serialization to strip proxies
  // This works because JSON.stringify unwraps Proxy objects to their underlying values
  if (x !== null && typeof x === 'object') {
    try {
      return JSON.parse(JSON.stringify(x)) as T;
    } catch (error) {
      // If JSON serialization fails (e.g., circular reference, function),
      // try structuredClone if available
      if (typeof structuredClone === 'function') {
        try {
          return structuredClone(x);
        } catch {
          // Both failed, return shallow copy as last resort
          return { ...x as Record<string, unknown> } as T;
        }
      }
      // No structuredClone, return shallow copy
      return { ...x as Record<string, unknown> } as T;
    }
  }

  // For primitives, use structuredClone if available (handles Date, etc.)
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(x);
    } catch {
      // Fallback for non-cloneable primitives
      return x;
    }
  }

  // Fallback: return as-is (primitives are already immutable)
  return x;
}

